import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import {
  HEADING_DEFECT,
  type HeadingDefect,
} from "contracts/enums/issue-semantics";
import { CONTENT_FITNESS_SUBCRITERION_KEY } from "contracts/report/content-fitness/subcriteria";
import { headingFact, wordingFact } from "../scan/issueSemantics";
import { clientIssueTitle, rationaleForIssueInSite } from "../scan/issueText";

type GenericIssueBriefPresentation = {
  title: string;
  objective: string;
  currentProblem: string;
  acceptanceCriteria: string[];
};

const HEADING_PROBLEMS: Record<HeadingDefect, string> = {
  [HEADING_DEFECT.MULTIPLE_PRIMARY]: "multiple primary headings",
  [HEADING_DEFECT.EMPTY]: "empty heading elements",
  [HEADING_DEFECT.SKIPPED_LEVELS]: "skipped heading levels",
  [HEADING_DEFECT.PRESENTATIONAL]:
    "heading elements used only for visual presentation",
};

export function genericIssueBriefPresentation(
  data: OverviewResponse,
  issue: Issue,
  recommendation: string,
): GenericIssueBriefPresentation {
  return {
    title: clientIssueTitle(issue),
    objective: `Apply the recorded recommendation within its stated scope: ${recommendation}`,
    currentProblem: issueProblem(data, issue),
    acceptanceCriteria: [
      "The recorded recommendation is implemented within its stated scope.",
      "All exceptions and preservation instructions in the recorded recommendation are respected.",
    ],
  };
}

function issueProblem(data: OverviewResponse, issue: Issue): string {
  const rationale = rationaleForIssueInSite(data, issue);
  if (issue.originResourceKind) return rationale;
  const wording = wordingFact(issue);
  if (wording?.correction) {
    const { incorrect, correct } = wording.correction;
    return `The source proposes changing “${incorrect}” to “${correct}”. ${rationale}`;
  }
  const heading = headingFact(issue);
  if (
    heading &&
    issue.primarySubcriterionKey ===
      CONTENT_FITNESS_SUBCRITERION_KEY.HEADING_HIERARCHY
  ) {
    const problems = [...new Set(heading.defects)].map(
      (defect) => HEADING_PROBLEMS[defect],
    );
    return `The source records ${problems.join("; ")}. ${rationale}`;
  }
  return rationale;
}
